// useSourceDocuments — fetches /source-documents with filters + exposes refetch().
//
// Mirrors useKBEntries / useTenants / useUsers exactly: re-fetch on filter
// change, no SWR cache. refetch() exposed for parent-driven invalidation
// after mutations.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  ListSourceDocumentsQuery,
  ListSourceDocumentsResponse,
} from '@gc-coach/shared';
import { listSourceDocuments } from '../api/endpoints.js';

export interface UseSourceDocumentsResult {
  data: ListSourceDocumentsResponse | null;
  isLoading: boolean;
  errorCode: string | null;
  refetch: () => Promise<void>;
}

export function useSourceDocuments(
  query: ListSourceDocumentsQuery,
): UseSourceDocumentsResult {
  const [data, setData] = useState<ListSourceDocumentsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const key = useMemo(
    () =>
      `${query.status ?? ''}|${query.language ?? ''}|${query.q?.trim() ?? ''}`,
    [query.status, query.language, query.q],
  );

  const fetchNow = useCallback(async () => {
    setIsLoading(true);
    const result = await listSourceDocuments(query);
    if (result.ok) {
      setData(result.data);
      setErrorCode(null);
    } else {
      setErrorCode(result.code);
    }
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    void fetchNow();
  }, [fetchNow]);

  return { data, isLoading, errorCode, refetch: fetchNow };
}
